import type {
  MesProduct,
  MesProductOptionField,
  MesProductPayload,
  MesProductSummary,
} from '@/types/cgt'

/**
 * Product Codes (z8nrz7fvhx): the rules every screen that shows one shares.
 * Pure, so they are tested here rather than in a component.
 */

// ─── Which description reads first ───────────────────────────────────────────

/**
 * Description 1 and 2 are typically the same description in two languages.
 * Each user picks which one reads first (Profile), stored as a preference that
 * follows them across tenants and devices.
 */
export const PRIMARY_DESCRIPTION_PREF_KEY = 'products.primaryDescription'

export type PrimaryDescription = 1 | 2

/** Anything stored that is not 2 reads as the default, Description 1. */
export function coercePrimaryDescription(raw: unknown): PrimaryDescription {
  return raw === 2 ? 2 : 1
}

export function isDefaultPrimaryDescription(value: PrimaryDescription): boolean {
  return value === 1
}

/**
 * The description to show, and the one to show on hover. When the chosen one
 * is empty the other stands in, so a code is never shown alone while a
 * description exists; `other` is then empty, and there is no hover.
 */
export function productDescriptions(
  product: Pick<MesProductSummary, 'description1' | 'description2'>,
  primary: PrimaryDescription,
): { primary: string; other: string } {
  const [first, second] =
    primary === 2
      ? [product.description2.trim(), product.description1.trim()]
      : [product.description1.trim(), product.description2.trim()]
  return first ? { primary: first, other: second } : { primary: second, other: '' }
}

/** "CT-DP-0101 – Ciltacabtagene autoleucel (cilta-cel) DP", or the code alone. */
export function productLabel(product: MesProductSummary, primary: PrimaryDescription): string {
  const text = productDescriptions(product, primary).primary
  return text ? `${product.code} – ${text}` : product.code
}

/**
 * What a picker searches besides the label: the code, both descriptions and
 * each of their words, so a word from either language finds the product.
 */
export function productKeywords(product: MesProductSummary): string[] {
  const words = `${product.description1} ${product.description2}`.split(/[\s,;()/]+/).filter(w => w.length > 1)
  return [product.code, product.description1, product.description2, ...words].filter(Boolean)
}

/** Matches the code or either description, so either language finds it. */
export function matchesProduct(product: MesProductSummary, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [product.code, product.description1, product.description2].some(v => v.toLowerCase().includes(q))
}

// ─── The form ────────────────────────────────────────────────────────────────

export type MesProductFieldKind = 'text' | 'option' | 'number' | 'storage'

export interface MesProductFieldDef {
  key: keyof MesProductDraft
  label: string
  kind: MesProductFieldKind
}

/**
 * The 24 fields, in the four sections of the spec's Product Information mock
 * and in its order. The Configure form and the dialog opened from an MES run
 * both render this, so they cannot differ.
 */
export const MES_PRODUCT_SECTIONS: { title: string; fields: MesProductFieldDef[] }[] = [
  {
    title: 'Identification',
    fields: [
      { key: 'code', label: 'Product Code', kind: 'text' },
      { key: 'description1', label: 'Product Description 1', kind: 'text' },
      { key: 'description2', label: 'Product Description 2', kind: 'text' },
      { key: 'productType', label: 'Product Type', kind: 'option' },
      { key: 'productNature', label: 'Product Nature', kind: 'option' },
      { key: 'clinicalOrCommercial', label: 'Clinical or Commercial', kind: 'option' },
    ],
  },
  {
    title: 'Formulation & Packaging',
    fields: [
      { key: 'dosageForm', label: 'Dosage Form', kind: 'option' },
      { key: 'dosage', label: 'Dosage (or Concentration)', kind: 'text' },
      { key: 'temperatureSpecification', label: 'Temperature Specification', kind: 'text' },
      { key: 'quantityPerPrimaryPackaging', label: 'Quantity per Primary Packaging', kind: 'number' },
      { key: 'containerType', label: 'Container Type', kind: 'option' },
      { key: 'primaryPackagingDimensions', label: 'Primary Packaging Dimensions', kind: 'text' },
    ],
  },
  {
    title: 'Transport Classification',
    fields: [
      { key: 'storagePackagingDimensions', label: 'Storage Packaging Dimensions', kind: 'text' },
      { key: 'adrIataClassification', label: 'ADR/IATA Classification', kind: 'option' },
      { key: 'adrIataClassificationOther', label: 'ADR/IATA Classification (if Other)', kind: 'option' },
      { key: 'minTransportTemperature', label: 'Minimum Transport Temperature', kind: 'text' },
      { key: 'maxTransportTemperature', label: 'Maximum Transport Temperature', kind: 'text' },
      { key: 'storageTemperature', label: 'Storage Temperature', kind: 'text' },
    ],
  },
  {
    title: 'Storage & References',
    fields: [
      { key: 'dedicatedStorageUnitId', label: 'Dedicated Storage Unit', kind: 'storage' },
      { key: 'mirrorStorageUnitId', label: 'Mirror Storage', kind: 'storage' },
      { key: 'comments', label: 'Comments', kind: 'text' },
      { key: 'qualiosLink', label: 'Qualios Link (SOP/WI in our GED)', kind: 'text' },
      { key: 'specificInstructions', label: 'Specific Instructions', kind: 'text' },
      { key: 'projectCode', label: 'Project Code', kind: 'option' },
    ],
  },
]

/** The form's values, all strings while typed; storage units by id ('' for none). */
export interface MesProductDraft {
  code: string
  description1: string
  description2: string
  productType: string
  productNature: string
  clinicalOrCommercial: string
  dosageForm: string
  dosage: string
  temperatureSpecification: string
  quantityPerPrimaryPackaging: string
  containerType: string
  primaryPackagingDimensions: string
  storagePackagingDimensions: string
  adrIataClassification: string
  adrIataClassificationOther: string
  minTransportTemperature: string
  maxTransportTemperature: string
  storageTemperature: string
  dedicatedStorageUnitId: string
  mirrorStorageUnitId: string
  comments: string
  qualiosLink: string
  specificInstructions: string
  projectCode: string
}

const STRING_KEYS = MES_PRODUCT_SECTIONS.flatMap(s => s.fields)
  .filter(f => f.kind === 'text' || f.kind === 'option')
  .map(f => f.key) as Exclude<keyof MesProductDraft, 'quantityPerPrimaryPackaging' | 'dedicatedStorageUnitId' | 'mirrorStorageUnitId'>[]

export function mesProductDraft(p?: MesProduct | null): MesProductDraft {
  const draft = Object.fromEntries(STRING_KEYS.map(k => [k, p ? (p[k] ?? '') : ''])) as unknown as MesProductDraft
  draft.quantityPerPrimaryPackaging = p?.quantityPerPrimaryPackaging != null ? String(p.quantityPerPrimaryPackaging) : ''
  draft.dedicatedStorageUnitId = p?.dedicatedStorageUnit?.id ?? ''
  draft.mirrorStorageUnitId = p?.mirrorStorageUnit?.id ?? ''
  return draft
}

export type MesProductFormErrors = Partial<Record<keyof MesProductDraft, string>>

/** What stops a save, by field. Empty when it can be saved. */
export function validateMesProduct(draft: MesProductDraft, adrIataOther: string): MesProductFormErrors {
  const errors: MesProductFormErrors = {}
  if (!draft.code.trim()) errors.code = 'Product Code is required.'
  else if (/[\r\n\t]/.test(draft.code)) errors.code = 'Product Code must be a single line.'
  const qty = draft.quantityPerPrimaryPackaging.trim()
  if (qty && !(Number.isFinite(Number(qty)) && Number(qty) >= 0)) {
    errors.quantityPerPrimaryPackaging = 'Enter a number, 0 or more.'
  }
  if (draft.dedicatedStorageUnitId && draft.dedicatedStorageUnitId === draft.mirrorStorageUnitId) {
    errors.mirrorStorageUnitId = 'Choose a different unit from the Dedicated Storage Unit.'
  }
  if (draft.adrIataClassificationOther && draft.adrIataClassification !== adrIataOther) {
    errors.adrIataClassificationOther = `Only when ADR/IATA Classification is "${adrIataOther}".`
  }
  return errors
}

function payloadValue(draft: MesProductDraft, key: keyof MesProductDraft): MesProductPayload[keyof MesProductPayload] {
  if (key === 'quantityPerPrimaryPackaging') {
    const qty = draft.quantityPerPrimaryPackaging.trim()
    return qty === '' ? null : Number(qty)
  }
  if (key === 'dedicatedStorageUnitId' || key === 'mirrorStorageUnitId') return draft[key] || null
  return draft[key].trim()
}

/**
 * What to send. A create sends every field; an edit sends only what changed
 * from `original`, never the code. "(if Other)" is sent empty once the
 * classification is no longer "Other", which is what the server keeps.
 */
export function mesProductPayload(
  draft: MesProductDraft,
  original: MesProductDraft | null,
  adrIataOther: string,
): MesProductPayload {
  const next: MesProductDraft = {
    ...draft,
    adrIataClassificationOther: draft.adrIataClassification === adrIataOther ? draft.adrIataClassificationOther : '',
  }
  const keys = (Object.keys(next) as (keyof MesProductDraft)[]).filter(k => !(original && k === 'code'))
  const out: Record<string, unknown> = {}
  for (const k of keys) {
    const value = payloadValue(next, k)
    if (original && value === payloadValue(original, k)) continue
    out[k] = value
  }
  return out as MesProductPayload
}

/** The option fields, for the reference lists. */
export function isOptionField(key: keyof MesProductDraft): key is MesProductOptionField {
  return MES_PRODUCT_SECTIONS.some(s => s.fields.some(f => f.key === key && f.kind === 'option'))
}

// ─── Showing values ──────────────────────────────────────────────────────────

/** An http(s) address to open, or null when the Qualios field holds a plain reference. */
export function qualiosHref(value: string): string | null {
  const v = value.trim()
  if (!/^https?:\/\//i.test(v)) return null
  try {
    return new URL(v).toString()
  } catch {
    return null
  }
}

/** Mirror Storage reads as the mock does: "Yes – LN2-FRZ-07 · Backup site", or "No". */
export function mirrorStorageText(unitText: string | null | undefined): string {
  return unitText ? `Yes – ${unitText}` : 'No'
}
